import React from 'react';
import { AuthContext } from '../../../helpers/AuthContext';
import {
  Avatar,
  Button,
  Typography,
  Box,
  Tooltip,
  Chip,
  Stack,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Collapse,
  Checkbox,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import useWorkflowDisplaySettings from '../../../hooks/useWorkflowDisplaySettings';

 
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BrushOutlinedIcon from '@mui/icons-material/BrushOutlined';
import InventoryIcon from '@mui/icons-material/Inventory';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';

import ShippingInfo from './ShippingInfo';
import { getInHouseDecoratorLogo } from '../../../utils/shippingBrandLogos';
import { UITableShell, UITableToolbar } from '../../../components/ui';
import { getShippingSupplierKey } from '../bulkShippingSelection';

import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';

 
const buildMethodMap = (asiLiveRawData) => {
  const vals =
    asiLiveRawData?.Imprinting?.Methods?.Values ||
    asiLiveRawData?.Imprinting?.Methods ||
    [];
  return (Array.isArray(vals) ? vals : []).reduce((m, v) => {
    const codeRaw = (v?.Code ?? v?.code ?? v?.value ?? v)?.toString?.() ?? '';
    const name = (v?.Name ?? v?.name ?? v?.label ?? v)?.toString?.() ?? '';
    const codeUC = codeRaw.trim().toUpperCase();
    const codeNoDollar = codeUC.replace(/^\$/, '');
    if (codeUC) m[codeUC] = name || codeRaw;
    if (codeNoDollar) m[codeNoDollar] = name || codeRaw;
    return m;
  }, {});
};

const toYMD = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(s.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
};

const parseDecorationSource = (src) => {
  const s = String(src ?? '').trim();
  if (!s) return { type: 'none', code: null };
  const parts = s.split(/[|:]/).map((p) => p.trim());
  const isType = (p) => /^(supplier|elite|inhouse|in-house)$/i.test(p);
  let type = parts.find(isType)?.toLowerCase() ?? null;
  let code = parts.find((p) => !isType(p)) || null;
  if (!type && /^elite(\s|$)/i.test(s)) type = 'elite';
  if (!type && !code) code = s;
  return { type: type ?? (code ? 'supplier' : 'none'), code };
};

const labelForSource = (src, methodMap) => {
  const { type, code } = parseDecorationSource(src);
  if (type === 'elite') return 'Elite Decorations (In-House)';
  if (!code) return null;
  const keyUC = String(code).trim().toUpperCase();
  const keyNoDollar = keyUC.replace(/^\$/, '');
  const methodName = methodMap[keyUC] || methodMap[keyNoDollar];
  const base = methodName || code;
  return type === 'supplier' ? `${base} (Supplier)` : base;
};

const unique = (arr) => Array.from(new Set(arr.filter(Boolean)));

const LEGACY_SO_TO_CANONICAL = {
  'sales order ready': 'Client Approved',
  'customer signed': 'Client Approved',
  completed: 'Client Approved',
  'sent for agreement': 'Agreement Pending',
  rejected: 'Client Change Requested',
  'changes requested': 'Client Change Requested',
};

const canonicalizeSoForShipping = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return LEGACY_SO_TO_CANONICAL[raw.toLowerCase()] || raw;
};

const SHIPPING_AVAILABLE_SO_STATUSES = new Set([
  'Estimate Sent', 'Agreement Pending', 'Agreement Sent',
  'Client Approved', 'In Production', 'Shipped',
  'Ready to be Invoiced', 'Invoiced', 'Paid', 'Closed',
]);

const SHOW_DECORATOR_CONFIRM = false;

const LABELS = {
  supplier: {
    confirmBtn: 'Confirm Shipping',
    confirmTooltipReady: 'Confirm supplier shipping details',
    confirmTooltipMissing: 'Fill in required supplier shipping fields first',
    revokeBtn: 'Change Shipping',
  },
  decorator: {
    confirmBtn: 'Confirm Decorator',
    confirmTooltipReady: 'Confirm decorator shipping details',
    confirmTooltipMissing: 'Fill in required decorator shipping fields first',
    revokeBtn: 'Change Shipping',
  },
};

const normalizeClientAddress = (d) => {
  if (!d) return null;
  const src =
    d.ClientAddress || d.ShipTo?.ClientAddress ||
    d.ClientRawAddress || d.ShipTo?.ClientRawAddress || null;
  if (!src) return null;
  if (src.streetAddress || src.city || src.stateProvince || src.postalZipCode || src.country || src.addressName) {
    return src;
  }
  return {
    addressName: src.addressName || src.name || '',
    companyName: src.companyName || '',
    streetAddress: src.street || src.streetAddress || '',
    suiteApt: src.suiteApt || '',
    optional1: src.optional1 || '',
    optional2: src.optional2 || '',
    city: src.city || '',
    stateProvince: src.state || src.stateProvince || '',
    country: src.country || '',
    postalZipCode: src.postal || src.postalZipCode || '',
  };
};

const hasDecoratorDetails = (d) => {
  if (!d) return false;
  const hasMethod = Boolean(d.Method || d.ShippingMethod);
  const hasDate = Boolean(d.InhandsDate);
  const addrCandidate =
    d.Address || d.ClientAddress || d.ClientRawAddress ||
    d.ShipTo?.ClientAddress || d.ShipTo?.ClientRawAddress;
  const hasAddress = !!(
    addrCandidate &&
    (addrCandidate.street || addrCandidate.streetAddress ||
      addrCandidate.city || addrCandidate.postal || addrCandidate.postalZipCode)
  );
  return hasMethod || hasDate || hasAddress;
};

const buildDecoratorDisplay = (decoratorShipping) => {
  if (!decoratorShipping) return null;
  const hasClientAddress =
    decoratorShipping.ClientAddress || decoratorShipping.ClientRawAddress ||
    decoratorShipping.ShipTo?.ClientAddress || decoratorShipping.ShipTo?.ClientRawAddress;
  const clientAddr = hasClientAddress ? normalizeClientAddress(decoratorShipping) : null;
  const shipToTypeRaw =
    decoratorShipping.ShipToType || decoratorShipping.Type || decoratorShipping.ShipTo?.Type || '';
  const isShipToClient = /client/i.test(String(shipToTypeRaw));
  return {
    ...decoratorShipping,
    SupplierName: decoratorShipping.SupplierName || decoratorShipping.DecoratorName || decoratorShipping.Decorator || '',
    ShipTo: isShipToClient && clientAddr ? 'Client Address' : decoratorShipping.ShipTo || '',
    Address: isShipToClient && clientAddr ? clientAddr : decoratorShipping.Address || null,
  };
};

 
const SHELL_PAPER_SX = {
  borderRadius: '14px',
  border: '1px solid',
  borderColor: 'divider',
  borderTop: '3px solid',
  borderTopColor: 'primary.main',
  boxShadow: '0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px -1px rgba(0,0,0,0.07)',
  overflow: 'hidden',
};

 
const ApprovalBadge = ({ approved }) =>
  approved ? (
    <Chip
      icon={<TaskAltIcon sx={{ fontSize: '13px !important' }} />}
      label="Approved"
      size="small"
      sx={{
        height: 24, fontWeight: 700, fontSize: '0.7rem',
        bgcolor: 'var(--ec-status-success-bg)',
        color: 'var(--ec-status-success-text)',
        border: '1px solid var(--ec-status-success-border)',
        '.MuiChip-icon': { color: 'var(--ec-status-success-text)', ml: '6px' },
        '.MuiChip-label': { px: '8px' },
      }}
    />
  ) : (
    <Chip
      icon={<WarningAmberIcon sx={{ fontSize: '13px !important' }} />}
      label="Pending"
      size="small"
      sx={{
        height: 24, fontWeight: 700, fontSize: '0.7rem',
        bgcolor: 'var(--ec-status-warning-bg)',
        color: 'var(--ec-status-warning-text)',
        border: '1px solid var(--ec-status-warning-border)',
        '.MuiChip-icon': { color: 'var(--ec-status-warning-text)', ml: '6px' },
        '.MuiChip-label': { px: '8px' },
      }}
    />
  );

export const shouldShowShippingApprovalGate = (requireShippingConfirmation) =>
  requireShippingConfirmation === true;

const PanelHeader = ({ icon: Icon, title, accentColor, isComplete, hasData }) => (
  <Box
    sx={{
      display: 'flex', alignItems: 'center', gap: 1.25,
      mb: 2, pb: 1.25, position: 'relative',
      borderBottom: `1.5px solid ${accentColor}20`,
      '&::after': {
        content: '""', position: 'absolute', bottom: -1.5,
        left: 0, width: 40, height: 1.5,
        bgcolor: accentColor, borderRadius: 99,
      },
    }}
  >
    <Box
      sx={{
        width: 32, height: 32, borderRadius: '8px',
        bgcolor: `${accentColor}18`,
        border: `1px solid ${accentColor}28`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: 15, color: accentColor }} />
    </Box>
    <Typography
      variant="overline"
      sx={{ fontWeight: 800, fontSize: '0.68rem', letterSpacing: '0.09em', color: 'text.secondary', lineHeight: 1 }}
    >
      {title}
    </Typography>
    {hasData && (
      <Box sx={{ ml: 'auto', flexShrink: 0 }}>
        {isComplete ? (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: '10px !important' }} />}
            label="Complete"
            size="small"
            sx={{
              height: 20, fontSize: '0.62rem', fontWeight: 700,
              bgcolor: 'var(--ec-status-success-bg)', color: 'var(--ec-status-success-text)',
              border: '1px solid var(--ec-status-success-border)',
              '.MuiChip-label': { px: '6px' },
              '.MuiChip-icon': { color: 'var(--ec-status-success-text)', ml: '4px' },
            }}
          />
        ) : (
          <Chip
            icon={<ErrorOutlineIcon sx={{ fontSize: '10px !important' }} />}
            label="Incomplete"
            size="small"
            sx={{
              height: 20, fontSize: '0.62rem', fontWeight: 700,
              bgcolor: 'var(--ec-status-warning-bg)', color: 'var(--ec-status-warning-text)',
              border: '1px solid var(--ec-status-warning-border)',
              '.MuiChip-label': { px: '6px' },
              '.MuiChip-icon': { color: 'var(--ec-status-warning-text)', ml: '4px' },
            }}
          />
        )}
      </Box>
    )}
  </Box>
);

const PanelPlaceholder = ({ icon: Icon, accentColor, message }) => (
  <Box
    sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', py: 4, px: 2, textAlign: 'center',
      borderRadius: '8px', border: '1.5px dashed', borderColor: 'divider',
      bgcolor: 'background.default', minHeight: 100,
    }}
  >
    <Icon sx={{ fontSize: 26, mb: 1, color: accentColor, opacity: 0.2 }} />
    <Typography
      variant="caption"
      sx={{ color: 'text.disabled', fontStyle: 'italic', lineHeight: 1.65, fontSize: '0.73rem', maxWidth: 200 }}
    >
      {message}
    </Typography>
  </Box>
);

const FullEmptyState = ({ icon: Icon, title, description, action }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12, px: 4, textAlign: 'center' }}>
    <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
      <Icon sx={{ fontSize: 38, opacity: 0.18, color: 'text.disabled' }} />
    </Box>
    <Typography variant="h6" fontWeight={700} sx={{ color: 'text.secondary', mb: 0.75 }}>{title}</Typography>
    <Typography variant="body2" sx={{ color: 'text.disabled', maxWidth: 360, lineHeight: 1.75, mb: action ? 3 : 0 }}>{description}</Typography>
    {action}
  </Box>
);
 


const ShippingToolbar = ({
  count,
  approvedCount,
  onRefresh,
  allExpanded,
  onExpandAll,
  onCollapseAll,
  bulkAvailable,
  validBulkSelection,
  selectedCount,
  onBulkEdit,
  onGenerateMissing,
  generatingMissing = false,
  canGenerateMissing = false,
  reviewMissingOnly = false,
  requireShippingConfirmation,
}) => {
  const showApprovalGate = shouldShowShippingApprovalGate(requireShippingConfirmation);
  const pendingCount = count - approvedCount;
  const pct = count > 0 ? Math.round((approvedCount / count) * 100) : 0;

  return (
    <Stack spacing={1.25}>
     
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
        <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
          <Box
            sx={{
              width: 32, height: 32, borderRadius: '8px',
              bgcolor: 'var(--ec-color-primary-soft)',
              border: '1px solid var(--ec-color-primary-border-soft)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <LocalShippingIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', lineHeight: 1 }}>
            Shipping Details
          </Typography>
          {count != null && count > 0 && (
            <Chip
              label={`${count} record${count !== 1 ? 's' : ''}`}
              size="small"
              sx={{
                fontWeight: 600, fontSize: '0.72rem', height: 22,
                bgcolor: 'var(--ec-status-neutral-bg)', color: 'var(--ec-status-neutral-text)',
                border: '1px solid var(--ec-status-neutral-border)',
              }}
            />
          )}
          {showApprovalGate && approvedCount > 0 && (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: '13px !important' }} />}
              label={`${approvedCount} approved`}
              size="small"
              sx={{
                height: 22, fontSize: '0.7rem', fontWeight: 700,
                backgroundColor: 'var(--ec-status-success-bg)', color: 'var(--ec-status-success-text)',
                border: '1px solid var(--ec-status-success-border)',
                '.MuiChip-label': { px: '7px' },
                '.MuiChip-icon': { color: 'var(--ec-status-success-text)', ml: '5px' },
              }}
            />
          )}
          {showApprovalGate && pendingCount > 0 && (
            <Chip
              icon={<WarningAmberIcon sx={{ fontSize: '13px !important' }} />}
              label={`${pendingCount} pending`}
              size="small"
              sx={{
                height: 22, fontSize: '0.7rem', fontWeight: 700,
                backgroundColor: 'var(--ec-status-warning-bg)', color: 'var(--ec-status-warning-text)',
                border: '1px solid var(--ec-status-warning-border)',
                '.MuiChip-label': { px: '7px' },
                '.MuiChip-icon': { color: 'var(--ec-status-warning-text)', ml: '5px' },
              }}
            />
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.75}>
          {count > 0 && canGenerateMissing && (
            <Tooltip title={reviewMissingOnly
              ? "Review unresolved approved Sales Order lines. Existing historical Shipping, POs and proofs are preserved."
              : "Create records for approved Sales Order items without shipping records. Existing shipping and POs are not changed."} arrow>
              <span><Button size="small" variant="outlined" disabled={generatingMissing}
                startIcon={<AddCircleOutlineIcon sx={{ fontSize: "15px !important" }} />}
                onClick={onGenerateMissing}
                sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600, height: 32, fontSize: "0.78rem" }}>
                {generatingMissing ? "Checking…" : (reviewMissingOnly ? "Review Missing Records" : "Generate Missing Records")}
              </Button></span>
            </Tooltip>
          )}
          {count > 0 && (
            <>
              <Tooltip title={allExpanded ? 'Collapse all cards' : 'Expand all cards'} arrow>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    allExpanded
                      ? <UnfoldLessIcon sx={{ fontSize: '15px !important' }} />
                      : <UnfoldMoreIcon sx={{ fontSize: '15px !important' }} />
                  }
                  onClick={allExpanded ? onCollapseAll : onExpandAll}
                  sx={{
                    textTransform: 'none', fontWeight: 600, borderRadius: '8px',
                    height: 32, fontSize: '0.78rem', px: 1.5,
                    borderColor: 'divider', color: 'text.secondary',
                    '&:hover': {
                      borderColor: 'primary.main', color: 'primary.main',
                      bgcolor: 'var(--ec-color-primary-soft)',
                    },
                  }}
                >
                  {allExpanded ? 'Collapse All' : 'Expand All'}
                </Button>
              </Tooltip>
              {bulkAvailable && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!validBulkSelection}
                  startIcon={<EditOutlinedIcon sx={{ fontSize: '15px !important' }} />}
                  onClick={onBulkEdit}
                  sx={{
                    textTransform: 'none', fontWeight: 600, borderRadius: '8px',
                    height: 32, fontSize: '0.78rem', px: 1.5,
                  }}
                >
                  {validBulkSelection ? `Edit Selected (${selectedCount})` : 'Edit Selected'}
                </Button>
              )}
              <Divider orientation="vertical" flexItem sx={{ height: 20, alignSelf: 'center', mx: 0.25 }} />
            </>
          )}
          <Tooltip title="Refresh" arrow>
            <span>
              <IconButton
                size="small"
                onClick={onRefresh}
                sx={{
                  color: 'text.secondary', border: '1px solid', borderColor: 'divider',
                  borderRadius: '8px', width: 32, height: 32,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <RefreshIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

 
      {showApprovalGate && count > 0 && (
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ flex: 1, height: 4, borderRadius: 99, bgcolor: 'action.selected', overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: `${pct}%`,
                bgcolor: 'var(--ec-status-success-text)',
                borderRadius: 99,
                transition: 'width 0.4s ease',
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, fontSize: '0.7rem', minWidth: 32, textAlign: 'right' }}>
            {pct}%
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};






 
const ShippingTable = ({
  shippingData,
  orderId,
  orderStatus,
  onGenerateShipping,
  getItemKey,
  handleEditClick,
  handleRowSelect,
  isItemSelected,
  onRefresh,
  bulkSelection,
  onBulkEdit,
  requireShippingConfirmation = false,
}) => {
  const { instanceUniqueId } = useParams();
  const { authState } = React.useContext(AuthContext);
  const currentUser = authState?.user || authState || {};
  const userRole = String(currentUser?.role || '').trim().toLowerCase().replace(/[_\s]+/g, '-');
  const canOverridePoProof = ['administrator', 'admin', 'super-administrator', 'superadmin'].includes(userRole);
  const canViewShippingDebug = canOverridePoProof;
  const { getWorkflowLabel, getWorkflowTooltip } = useWorkflowDisplaySettings(instanceUniqueId);
  const theme = useTheme();
  const showApprovalGate = shouldShowShippingApprovalGate(requireShippingConfirmation);

 
  const SUPPLIER_ACCENT = theme.palette.primary.main;
  const DECORATOR_ACCENT = theme.palette.success?.main || '#0F766E';

  const [exceptionSaving, setExceptionSaving] = React.useState(null);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const [generatingMissing, setGeneratingMissing] = React.useState(false);
  const [missingPreview, setMissingPreview] = React.useState(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewVersion, setPreviewVersion] = React.useState(0);
  const previewRequestRef = React.useRef(0);
  const eligibleForMissing = SHIPPING_AVAILABLE_SO_STATUSES.has(canonicalizeSoForShipping(orderStatus));
  const previewUrl = `${process.env.REACT_APP_NODEJS_SERVER_URL}/shipping/order/${orderId}/missing-preview`;

  React.useEffect(() => {
    const requestId = ++previewRequestRef.current;
    setMissingPreview(null);
    setPreviewOpen(false);
    if (!eligibleForMissing || !orderId || !shippingData?.length) return undefined;
    axios.get(previewUrl, { headers: { accessToken: localStorage.getItem('accessToken') } })
      .then((response) => {
        if (previewRequestRef.current === requestId) setMissingPreview(response?.data?.data || null);
      })
      .catch(() => {
        if (previewRequestRef.current === requestId) setMissingPreview(null);
      });
    return () => { previewRequestRef.current += 1; };
  }, [orderId, eligibleForMissing, previewUrl, previewVersion, shippingData?.length]);

  const openMissingPreview = async () => {
    if (previewLoading || generatingMissing) return;
    setPreviewLoading(true);
    setPreviewOpen(true);
    setMissingPreview(null);
    try {
      const response = await axios.get(previewUrl, { headers: { accessToken: localStorage.getItem('accessToken') } });
      setMissingPreview(response?.data?.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to check missing shipping records');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };
  const generateMissingShipping = async () => {
    if (generatingMissing || previewLoading || !missingPreview?.canGenerate) return;
    setGeneratingMissing(true);
    try {
      // Re-check approved Sales Order coverage immediately before creating anything.
      const latest = await axios.get(previewUrl, { headers: { accessToken: localStorage.getItem('accessToken') } });
      const current = latest?.data?.data;
      if (!current?.canGenerate) {
        setMissingPreview(current || null);
        toast('No eligible missing shipping records remain');
        return;
      }
      setMissingPreview(current);
      const response = await axios.post(`${process.env.REACT_APP_NODEJS_SERVER_URL}/shipping/order/${orderId}/generate-missing?instanceUniqueId=${encodeURIComponent(instanceUniqueId || '')}`, {},
        { headers: { accessToken: localStorage.getItem('accessToken') } });
      const count = Number(response?.data?.createdCount || 0);
      if (count) toast.success(`${count} missing shipping record${count !== 1 ? 's' : ''} created`);
      else toast('No missing shipping records found');
      setPreviewOpen(false);
      setMissingPreview(null);
      await rehydrateShippingView();
      setPreviewVersion((version) => version + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not generate missing shipping records');
    } finally {
      setGeneratingMissing(false);
    }
  };

 
  const [collapsedKeys, setCollapsedKeys] = React.useState(new Set());

  const toggleCard = (key) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleExpandAll = () => setCollapsedKeys(new Set());
  const handleCollapseAll = () => {
    if (!shippingData) return;
    setCollapsedKeys(new Set(shippingData.map((item, i) => getItemKey(item, i))));
  };

  const allExpanded = shippingData
    ? shippingData.every((item, i) => !collapsedKeys.has(getItemKey(item, i)))
    : true;

  const toggleProofPoOverride = async (item) => {
    if (!canOverridePoProof) {
      toast.error('Administrator or Super Administrator access required.');
      return;
    }
    if (!item.OrderItemId) {
      toast.error('This shipping record must be linked to a sales-order item first.');
      return;
    }
    const enabled = !item.ProofPoOverride;
    if (enabled && !window.confirm('Allow PO generation while artwork is pending for this product? Proof approvals will still be required for production.')) return;
    setExceptionSaving(item.id);
    try {
      await axios.patch(`${process.env.REACT_APP_NODEJS_SERVER_URL}/shipping/${item.id}/proof-po-override`,
        { enabled }, { headers: { accessToken: localStorage.getItem('accessToken') } });
      toast.success(enabled ? 'PO artwork exception enabled' : 'PO artwork exception removed');
      await rehydrateShippingView();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Unable to save PO artwork exception');
    } finally {
      setExceptionSaving(null);
    }
  };

  const rehydrateShippingView = React.useCallback(async () => {
    if (typeof onGenerateShipping === 'function') {
      await Promise.resolve(onGenerateShipping());
      return;
    }
    if (typeof onRefresh === 'function') {
      await Promise.resolve(onRefresh());
    }
  }, [onGenerateShipping, onRefresh]);


  const [previewImage, setPreviewImage] = React.useState(null); 

 
  if (!shippingData || shippingData.length === 0) {
    const canonicalSO = canonicalizeSoForShipping(orderStatus);

    if (!SHIPPING_AVAILABLE_SO_STATUSES.has(canonicalSO)) {
      return (
        <UITableShell customLayout paperSx={{ ...SHELL_PAPER_SX, display: 'flex', flexDirection: 'column' }}>
          <UITableToolbar>
            <ShippingToolbar onRefresh={onRefresh} count={0} approvedCount={0} allExpanded onExpandAll={() => {}} onCollapseAll={() => {}} requireShippingConfirmation={showApprovalGate} />
          </UITableToolbar>
          <Box sx={{ flex: 1 }}>
            <Tooltip title={getWorkflowTooltip('SO', canonicalSO || orderStatus) || ''} arrow>
              <Box sx={{ height: '100%' }}>
                <FullEmptyState
                  icon={LocalShippingIcon}
                  title="Shipping unavailable"
                  description={`Shipping records cannot be generated while the order status is "${getWorkflowLabel('SO', canonicalSO || orderStatus) || orderStatus}" — not ready. Update the order status to continue.`}
                />
              </Box>
            </Tooltip>
          </Box>
        </UITableShell>
      );
    }

    const handleGenerateShipping = async () => {
      try {
        await axios.post(`${process.env.REACT_APP_NODEJS_SERVER_URL}/estimation/${orderId}/shipping-records`);
        toast.success('Shipping records created');
        await rehydrateShippingView();
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to generate shipping records');
      }
    };

    return (
      <UITableShell customLayout paperSx={{ ...SHELL_PAPER_SX, display: 'flex', flexDirection: 'column' }}>
        <UITableToolbar>
          <ShippingToolbar onRefresh={onRefresh} count={0} approvedCount={0} allExpanded onExpandAll={() => {}} onCollapseAll={() => {}} requireShippingConfirmation={showApprovalGate} />
        </UITableToolbar>
        <Box sx={{ flex: 1 }}>
          <FullEmptyState
            icon={InventoryIcon}
            title="No shipping records yet"
            description="Generate shipping records to start filling in supplier and decorator details for each product in this order."
            action={
              <Button
                variant="contained"
                size="small"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleGenerateShipping}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none', fontSize: '0.85rem', px: 3, height: 38 }}
              >
                Generate Shipping Records
              </Button>
            }
          />
        </Box>
      </UITableShell>
    );
  }
 
  const requestDelete = (shippingId, approved) =>
    setDeleteTarget({ id: shippingId, approved: !!approved });

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleting(true);
      await axios.delete(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/shipping/${deleteTarget.id}`,
        { headers: { accessToken: localStorage.getItem('accessToken') } }
      );
      toast.success('Shipping record deleted');
      setDeleteTarget(null);
      setDeleting(false);
      await rehydrateShippingView();
    } catch (err) {
      setDeleting(false);
      toast.error(err?.response?.data?.message || 'Failed to delete record');
    }
  };

 
  const isComplete = (d) => {
    if (!d) return false;
    const toStr = (v) => (v == null ? '' : String(v).trim());
    const toBool = (v) => {
      if (typeof v === 'boolean') return v;
      const s = String(v).toLowerCase();
      return s === 'true' || s === '1' || s === 'yes';
    };
    const hasDateLike = (v) => {
      if (!v) return false;
      const s = String(v);
      return /^\d{4}-\d{2}-\d{2}$/.test(s) || !isNaN(Date.parse(s));
    };
    const method = toStr(d.ShippingMethod ?? d.Method ?? d.ShipTo?.ShippingMethod);
    const hasMethod = method.length > 0;
    const hasDate = hasDateLike(d.InhandsDate) || hasDateLike(d.ShipTo?.InhandsDate);
    const firmFlag = d.IsFirm ?? d.ShipTo?.IsFirm;
    const hasTiming = hasDate || toBool(firmFlag);
    const hasDestination = !!(
      d.ShipToId || d.ShipTo?.id || d.ShipTo?.AddressId || d.AddressId || d.Address?.id ||
      d.ShipTo?.ClientAddressId ||
      (d.ShipTo?.ClientRawAddress &&
        (d.ShipTo.ClientRawAddress.street || d.ShipTo.ClientRawAddress.city ||
          d.ShipTo.ClientRawAddress.state || d.ShipTo.ClientRawAddress.postal ||
          d.ShipTo.ClientRawAddress.country))
    );
    return hasMethod && hasDestination && hasTiming;
  };

 
  const approve = async (shippingId, label = '') => {
    await axios.post(
      `${process.env.REACT_APP_NODEJS_SERVER_URL}/shipping/${shippingId}/approve`,
      { note: `Confirmed ${label}` },
      { headers: { accessToken: localStorage.getItem('accessToken') } }
    );
    toast.dismiss(`revoke-${shippingId}`);
    toast.success(`${label || 'Shipping'} confirmed`, { id: `approve-${shippingId}` });
  };

  const revoke = async (shippingId, label = '') => {
    await axios.post(
      `${process.env.REACT_APP_NODEJS_SERVER_URL}/shipping/${shippingId}/revoke`,
      {},
      { headers: { accessToken: localStorage.getItem('accessToken') } }
    );
    toast.dismiss(`approve-${shippingId}`);
    toast(`${label || 'Shipping'} approval revoked`, { id: `revoke-${shippingId}` });
  };

  const approvedCount = shippingData.filter((i) => i.Approved).length;

 
  return (
    <>
      <UITableShell
        customLayout
        paperSx={{ ...SHELL_PAPER_SX, display: 'flex', flexDirection: 'column', minHeight: 0, mt: 0 }}
      >
        {/* Toolbar */}
        <UITableToolbar>
          <ShippingToolbar
            count={shippingData.length}
            approvedCount={approvedCount}
            onGenerateMissing={openMissingPreview}
            generatingMissing={previewLoading || generatingMissing}
            canGenerateMissing={eligibleForMissing && Number(missingPreview?.missingCount || 0) > 0}
            reviewMissingOnly={Boolean(missingPreview && !missingPreview.canGenerate)}
            onRefresh={onRefresh}
            allExpanded={allExpanded}
            onExpandAll={handleExpandAll}
            onCollapseAll={handleCollapseAll}
            bulkAvailable={bulkSelection?.bulkAvailable}
            validBulkSelection={bulkSelection?.validBulkSelection}
            selectedCount={bulkSelection?.selectedRows?.length || 0}
            onBulkEdit={onBulkEdit}
            requireShippingConfirmation={showApprovalGate}
          />
        </UITableToolbar>

        {/* Scrollable card area */}
        <Box
          sx={{
            flex: 1, overflowY: 'auto', p: 2.5,
            bgcolor: 'background.default',
            '&::-webkit-scrollbar': { width: 6, height: 6 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(148,163,184,0.6)', borderRadius: 99 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          }}
        >
          <Stack spacing={2}>
            {shippingData.map((item, index) => {
              const key = getItemKey(item, index);
              const isCollapsed = collapsedKeys.has(key);
              const supplierKey = getShippingSupplierKey(item);
              const bulkEligible = bulkSelection?.eligibleSupplierKeys?.has(supplierKey);
              const incompatibleSupplier = Boolean(
                bulkSelection?.activeSupplierKey && bulkSelection.activeSupplierKey !== supplierKey
              );

              const methodMap = buildMethodMap(item.asiLiveRawData);
              const hasDeco = hasDecoratorDetails(item.DecoratorShipping);

              const decoratorDisplayData = hasDeco && item.DecoratorShipping
                ? buildDecoratorDisplay({
                    ...item.DecoratorShipping,
                    InhandsDate: toYMD(item.DecoratorShipping?.InhandsDate),
                  })
                : null;

              const supplierDisplayData = item.SupplierShipping
                ? { ...item.SupplierShipping, InhandsDate: toYMD(item.SupplierShipping?.InhandsDate) }
                : null;

              const normalizeSrc = (src) => {
                if (typeof src === 'string') return src;
                if (src && typeof src === 'object') {
                  const t = src.type || src.source || src.decorator || null;
                  const c = src.code || src.imprintCode || src.methodCode || src.asiCode || null;
                  return [t, c].filter(Boolean).join('|');
                }
                return '';
              };

              const rawSources = (
                Array.isArray(item.decorationSources)
                  ? item.decorationSources
                  : item.decorationSources ? [item.decorationSources] : []
              ).map(normalizeSrc).filter(Boolean);

              const humanLabels = unique(rawSources.map((s) => labelForSource(s, methodMap)));
              const supplierComplete = isComplete(item.SupplierShipping);
              const decoratorComplete = isComplete(item.DecoratorShipping);

              return (
                <Paper
                  key={key}
                  elevation={0}
                  sx={{
                    borderRadius: '12px',
                    border: '1.5px solid',
                    borderColor: showApprovalGate && item.Approved
                      ? 'var(--ec-status-success-border)'
                      : 'var(--ec-color-border)',
                   
                    borderLeft: `3.5px solid ${
                      showApprovalGate && item.Approved ? 'var(--ec-status-success-text)' : SUPPLIER_ACCENT
                    }`,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    boxShadow: '0 1px 4px 0 rgba(0,0,0,0.05)',
                    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                    '&:hover': { boxShadow: '0 4px 16px 0 rgba(0,0,0,0.08)' },
                  }}
                >
 
                  <Box
                    sx={{
                      px: 2, py: 1.5,
                      bgcolor: 'background.default',
                      borderBottom: isCollapsed ? 'none' : '1px solid',
                      borderColor: 'divider',
                      display: 'flex', alignItems: 'center', gap: 2,
                    }}
                  >
                    {bulkEligible && (
                      <Tooltip
                        title={incompatibleSupplier ? 'Select products from one supplier at a time' : 'Select for bulk shipping edit'}
                        arrow
                      >
                        <span>
                          <Checkbox
                            size="small"
                            checked={isItemSelected(key)}
                            disabled={incompatibleSupplier}
                            onChange={() => handleRowSelect(key, item)}
                            inputProps={{ 'aria-label': `Select ${item.Name || 'product'} for bulk shipping edit` }}
                            sx={{ p: 0.5 }}
                          />
                        </span>
                      </Tooltip>
                    )}
                    {/* Product image */}
                    {/* <Box
                      sx={{
                        width: 52, height: 52, flexShrink: 0,
                        borderRadius: '8px',
                        border: '1px solid', borderColor: 'divider',
                        bgcolor: 'background.paper',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        overflow: 'hidden', p: '4px',
                      }}
                    >
                      <Avatar
                        src={item.ImageUrl}
                        alt={item.Name}
                        variant="square"
                        sx={{
                          width: 44, height: 44, borderRadius: '4px',
                          '.MuiAvatar-img': { objectFit: 'contain' },
                        }}
                      />
                    </Box> */}

                    <Tooltip title="Click to view full image" arrow placement="right">
  <Box
    onClick={() => item.ImageUrl && setPreviewImage({ src: item.ImageUrl, name: item.Name })}
    sx={{
      width: 52, height: 52, flexShrink: 0,
      borderRadius: '8px',
      border: '1.5px solid', borderColor: 'divider',
      bgcolor: 'background.paper',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden', p: '4px',
      cursor: item.ImageUrl ? 'zoom-in' : 'default',
      position: 'relative',
      transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
      '&:hover': item.ImageUrl ? {
        borderColor: 'primary.main',
        boxShadow: '0 0 0 3px var(--ec-color-primary-soft)',
        '& .zoom-icon-overlay': { opacity: 1 },
      } : {},
    }}
  >
    <Avatar
      src={item.ImageUrl}
      alt={item.Name}
      variant="square"
      sx={{
        width: 44, height: 44, borderRadius: '4px',
        '.MuiAvatar-img': { objectFit: 'contain' },
      }}
    />
    {/* Zoom icon overlay on hover */}
    {item.ImageUrl && (
      <Box
        className="zoom-icon-overlay"
        sx={{
          position: 'absolute', inset: 0,
          bgcolor: 'rgba(0,0,0,0.35)',
          borderRadius: '6px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: 0,
          transition: 'opacity 0.18s ease',
        }}
      >
        <ZoomInIcon sx={{ fontSize: 18, color: '#fff' }} />
      </Box>
    )}
  </Box>
</Tooltip>
 
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: 'text.primary', fontWeight: 700, lineHeight: 1.3,
                          mb: 0.75, letterSpacing: '-0.01em',
                          overflow: 'hidden', display: '-webkit-box',
                          WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {item.Name}
                      </Typography>
                      <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap gap={0.75}>
                        {showApprovalGate && <ApprovalBadge approved={item.Approved} />}
                        {humanLabels.length > 0 ? (
                          humanLabels.map((lbl, i) => (
                            <Box
                              key={`${lbl}-${i}`}
                              sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                bgcolor: alpha(theme.palette.primary.main, 0.06),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                                borderRadius: '99px', px: '9px', py: '3px',
                              }}
                            >
                              {getInHouseDecoratorLogo(instanceUniqueId, lbl) && (
                                <Box
                                  component="img"
                                  src={getInHouseDecoratorLogo(instanceUniqueId, lbl)}
                                  alt="Elite Decorations (In-House)"
                                  sx={{ width: 13, height: 13, borderRadius: '50%', flexShrink: 0 }}
                                />
                              )}
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{
                                  fontSize: '0.66rem', color: 'primary.main',
                                  fontStyle: 'italic', lineHeight: 1.3,
                                }}
                              >
                                ({i + 1}) {lbl}
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.7rem' }}
                          >
                            No decoration required
                          </Typography>
                        )}
                        {canOverridePoProof && humanLabels.some((label) => /in-house/i.test(label)) && (
                          <Tooltip title="Permit this sales-order item to generate POs with artwork pending; does not approve proofs." arrow>
                            <span>
                              <Button size="small" variant={item.ProofPoOverride ? 'contained' : 'outlined'}
                                color={item.ProofPoOverride ? 'warning' : 'inherit'}
                                disabled={exceptionSaving === item.id || !item.OrderItemId}
                                onClick={() => toggleProofPoOverride(item)}
                                sx={{ textTransform: 'none', fontSize: '0.68rem', py: 0, minHeight: 23 }}>
                                {item.ProofPoOverride ? 'Artwork Pending · PO Exception' : 'Allow PO with Artwork Pending'}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>

 
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                      <Box
                        sx={{
                          width: 28, height: 28, borderRadius: '50%',
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <Typography
                          sx={{ color: 'primary.main', fontWeight: 700, fontSize: '0.75rem', lineHeight: 1 }}
                        >
                          {index + 1}
                        </Typography>
                      </Box>
                      <Tooltip title={isCollapsed ? 'Expand card' : 'Collapse card'} arrow>
                        <IconButton
                          size="small"
                          onClick={() => toggleCard(key)}
                          sx={{
                            width: 28, height: 28, borderRadius: '7px',
                            border: '1px solid', borderColor: 'divider',
                            color: 'text.secondary',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.06),
                              borderColor: 'primary.main',
                              color: 'primary.main',
                            },
                          }}
                        >
                          {isCollapsed
                            ? <KeyboardArrowDownIcon sx={{ fontSize: 17 }} />
                            : <KeyboardArrowUpIcon sx={{ fontSize: 17 }} />
                          }
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>

 
                  <Collapse in={!isCollapsed} timeout={220} unmountOnExit={false}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        borderBottom: '1px solid', borderColor: 'divider',
                      }}
                    >
                      {/* Supplier Panel */}
                      <Box
                        sx={{
                          p: 2.5,
                          borderRight: { xs: 'none', md: '1px solid' },
                          borderBottom: { xs: '1px solid', md: 'none' },
                          borderColor: 'divider',
                        }}
                      >
                        <PanelHeader
                          icon={LocalShippingIcon}
                          title="Supplier Shipping"
                          accentColor={SUPPLIER_ACCENT}
                          isComplete={supplierComplete}
                          hasData={!!supplierDisplayData}
                        />
                        {supplierDisplayData ? (
                          <ShippingInfo type="Supplier" data={supplierDisplayData} isSupplier />
                        ) : (
                          <PanelPlaceholder
                            icon={LocalShippingIcon}
                            accentColor={SUPPLIER_ACCENT}
                            message="Not yet configured. Click Edit to set up supplier shipping details."
                          />
                        )}
                      </Box>

                      {/* Decorator Panel */}
                      <Box sx={{ p: 2.5 }}>
                        <PanelHeader
                          icon={BrushOutlinedIcon}
                          title="Decorator Shipping"
                          accentColor={DECORATOR_ACCENT}
                          isComplete={decoratorComplete}
                          hasData={!!decoratorDisplayData}
                        />
                        {decoratorDisplayData ? (
                          <ShippingInfo type="Decorator" data={decoratorDisplayData} />
                        ) : (
                          <PanelPlaceholder
                            icon={BrushOutlinedIcon}
                            accentColor={DECORATOR_ACCENT}
                            message={item.assignedEliteDecoratorId
                              ? item.missingEliteDecoratorRoute
                                ? "Elite Decoration is assigned on the Sales Order. Supplier → Decorator shipping is not configured; click Edit to complete this route."
                                : "Elite Decoration is assigned, but decorator shipping details are incomplete. Click Edit to complete the destination."
                              : "No decorator shipping required for this item."}
                          />
                        )}
                      </Box>
                    </Box>
                  </Collapse>

          
                  <Box
                    sx={{
                      px: 2, py: 1.5,
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5,
                      bgcolor: 'background.paper',
                    }}
                  >
 
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      {showApprovalGate && item?.SupplierShipping && (
                        item.Approved ? (
                          <Tooltip title="Revoke approval to make changes" arrow>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<LockOpenOutlinedIcon sx={{ fontSize: '15px !important' }} />}
                              onClick={async () => {
                                try { await revoke(item.id, 'Supplier'); onRefresh?.(); }
                                catch (e) { toast.error(e?.response?.data?.message || 'Failed to revoke supplier'); }
                              }}
                              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', height: 34, fontSize: '0.8rem' }}
                            >
                              {LABELS.supplier.revokeBtn}
                            </Button>
                          </Tooltip>
                        ) : (
                          <Tooltip arrow title={supplierComplete ? LABELS.supplier.confirmTooltipReady : LABELS.supplier.confirmTooltipMissing}>
                            <span>
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                disabled={!supplierComplete}
                                startIcon={<LockOutlinedIcon sx={{ fontSize: '15px !important' }} />}
                                onClick={async () => {
                                  try { await approve(item.id, 'Supplier'); onRefresh?.(); }
                                  catch (e) { toast.error(e?.response?.data?.message || 'Failed to confirm supplier'); }
                                }}
                                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', height: 34, boxShadow: 'none', fontSize: '0.8rem' }}
                              >
                                {LABELS.supplier.confirmBtn}
                              </Button>
                            </span>
                          </Tooltip>
                        )
                      )}

                      {showApprovalGate && decoratorDisplayData && SHOW_DECORATOR_CONFIRM && (
                        item.DecoratorShipping?.Approved ? (
                          <Tooltip title="Revoke decorator approval" arrow>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<LockOpenOutlinedIcon sx={{ fontSize: '15px !important' }} />}
                              onClick={async () => {
                                try {
                                  await revoke(item.id, 'Decorator');
                                  typeof onGenerateShipping === 'function' ? onGenerateShipping() : onRefresh?.();
                                } catch (e) {
                                  toast.error(e?.response?.data?.message || 'Failed to revoke decorator');
                                }
                              }}
                              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', height: 34, fontSize: '0.8rem' }}
                            >
                              {LABELS.decorator.revokeBtn}
                            </Button>
                          </Tooltip>
                        ) : (
                          <Tooltip arrow title={decoratorComplete ? LABELS.decorator.confirmTooltipReady : LABELS.decorator.confirmTooltipMissing}>
                            <span>
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                disabled={!decoratorComplete}
                                startIcon={<LockOutlinedIcon sx={{ fontSize: '15px !important' }} />}
                                onClick={async () => {
                                  try {
                                    await approve(item.id, 'Decorator');
                                    typeof onGenerateShipping === 'function' ? onGenerateShipping() : onRefresh?.();
                                  } catch (e) {
                                    toast.error(e?.response?.data?.message || 'Failed to confirm decorator');
                                  }
                                }}
                                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', height: 34, boxShadow: 'none', fontSize: '0.8rem' }}
                              >
                                {LABELS.decorator.confirmBtn}
                              </Button>
                            </span>
                          </Tooltip>
                        )
                      )}
                    </Stack>

          
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ height: 22, alignSelf: 'center', display: { xs: 'none', sm: 'block' } }}
                      />
                      <Tooltip title={showApprovalGate && item.Approved ? 'Approved — change shipping first to edit' : 'Edit shipping details'} arrow>
                        <span>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={showApprovalGate && item.Approved}
                            startIcon={<EditOutlinedIcon sx={{ fontSize: '15px !important' }} />}
                            onClick={() => handleEditClick(item.id)}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', height: 34, fontSize: '0.8rem' }}
                          >
                            Edit
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete this shipping record" arrow>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteOutlineIcon sx={{ fontSize: '15px !important' }} />}
                          onClick={() =>
                            requestDelete(item.id, Boolean(showApprovalGate && (item.Approved || item.DecoratorShipping?.Approved)))
                          }
                          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', height: 34, fontSize: '0.8rem' }}
                        >
                          Delete
                        </Button>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        </Box>

        {/* Footer summary bar */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2.5, py: 1,
            borderTop: '1px solid', borderColor: 'divider',
            bgcolor: 'background.paper',
            borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px',
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {shippingData.length} shipping record{shippingData.length !== 1 ? 's' : ''}
            {showApprovalGate && approvedCount > 0 && (
              <>
                {' · '}
                <Box component="span" sx={{ color: 'var(--ec-status-success-text)', fontWeight: 700 }}>
                  {approvedCount} approved
                </Box>
                {shippingData.length - approvedCount > 0 && (
                  <>
                    {' · '}
                    <Box component="span" sx={{ color: 'var(--ec-status-warning-text)' }}>
                      {shippingData.length - approvedCount} pending
                    </Box>
                  </>
                )}
              </>
            )}
          </Typography>
          {showApprovalGate && shippingData.length > 0 && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 72, height: 4, borderRadius: 99, bgcolor: 'action.selected', overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${(approvedCount / shippingData.length) * 100}%`,
                    bgcolor: 'var(--ec-status-success-text)',
                    borderRadius: 99,
                    transition: 'width 0.4s ease',
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600, fontSize: '0.68rem', minWidth: 28 }}>
                {Math.round((approvedCount / shippingData.length) * 100)}%
              </Typography>
            </Stack>
          )}
        </Box>
      </UITableShell>

      {canViewShippingDebug && (
        <Box sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Shipping Debug JSON (Administrators Only)
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify({ orderId, shippingRecords: shippingData || [] }, null, 2))
                  .then(() => toast.success('Shipping JSON copied'))
                  .catch(() => toast.error('Unable to copy shipping JSON'));
              }}
            >
              Copy JSON
            </Button>
          </Box>
          <Box
            component="pre"
            sx={{ m: 0, p: 2, maxHeight: 420, overflow: 'auto', fontSize: 12, lineHeight: 1.5, fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
          >
            {JSON.stringify({ orderId, shippingRecords: shippingData || [] }, null, 2)}
          </Box>
        </Box>
      )}

      <Dialog open={previewOpen} onClose={() => !generatingMissing && setPreviewOpen(false)}
        fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {missingPreview?.canGenerate
            ? `Generate ${missingPreview.missingCount} Missing Shipping Record${missingPreview.missingCount === 1 ? '' : 's'}`
            : 'Missing Shipping Records'}
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {previewLoading ? (
            <Typography variant="body2" color="text.secondary">
              Checking the approved Sales Order against existing shipping records…
            </Typography>
          ) : missingPreview ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.25}>
                <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                    {missingPreview.existingRecordCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Existing — untouched</Typography>
                </Box>
                <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                    {missingPreview.missingCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {missingPreview.canGenerate ? 'New records to create' : 'Lines needing attention'}
                  </Typography>
                </Box>
              </Stack>
              {missingPreview.missingProducts?.length > 0 && (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.75, maxHeight: 280, overflowY: 'auto' }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
                    {missingPreview.canGenerate ? 'New shipping records' : 'Sales Order lines to review'}
                  </Typography>
                  <Stack spacing={1.5}>
                    {missingPreview.missingProducts.map((product, index) => (
                      <Box key={product.cartLineId || product.orderItemId || index}>
                        <Typography variant="body2" fontWeight={600}>
                          {product.name}
                        </Typography>
                        {product.variant && (
                          <Typography variant="body2" color="text.secondary">{product.variant}</Typography>
                        )}
                        {product.blocked && (
                          <Typography variant="caption" sx={{ color: 'warning.main' }}>
                            {product.reason || 'This line needs attention before shipping can be generated.'}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
              {missingPreview.message && !missingPreview.canGenerate && (
                <Typography variant="body2" color="text.secondary">
                  {missingPreview.message}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {missingPreview.canGenerate
                  ? 'Only these missing records will be added. Review their shipping dates, addresses and accounts before generating any new purchase orders.'
                  : 'No records can be safely created yet. The existing shipping records and purchase orders remain unchanged.'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Existing shipping details, approvals, proofs and issued purchase orders are never regenerated by this action.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Unable to load the missing-record review. No changes have been made.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" disabled={generatingMissing} onClick={() => setPreviewOpen(false)}>
            {missingPreview?.canGenerate ? 'Cancel' : 'Close'}
          </Button>
          {missingPreview?.canGenerate && (
            <Button variant="contained" disabled={generatingMissing || previewLoading}
              onClick={generateMissingShipping}>
              {generatingMissing
                ? 'Generating…'
                : `Generate ${missingPreview.missingCount} Record${missingPreview.missingCount === 1 ? '' : 's'}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Image Preview Modal */}
<Dialog
  open={!!previewImage}
  onClose={() => setPreviewImage(null)}
  maxWidth="md"
  PaperProps={{
    sx: {
      borderRadius: '14px',
      overflow: 'hidden',
      bgcolor: 'background.paper',
      boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
    },
  }}
>
  <DialogTitle
    sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      py: 1.5, px: 2.5,
      borderBottom: '1px solid', borderColor: 'divider',
    }}
  >
    <Typography
      variant="subtitle2"
      fontWeight={700}
      sx={{
        overflow: 'hidden', display: '-webkit-box',
        WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
        color: 'text.primary', maxWidth: 420,
      }}
    >
      {previewImage?.name || 'Product Image'}
    </Typography>
    <IconButton
      size="small"
      onClick={() => setPreviewImage(null)}
      sx={{
        color: 'text.secondary', border: '1px solid', borderColor: 'divider',
        borderRadius: '8px', width: 30, height: 30, ml: 2,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <CloseIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </DialogTitle>

  <DialogContent
    sx={{
      p: 3, display: 'flex', alignItems: 'center',
      justifyContent: 'center', bgcolor: 'background.default',
      minHeight: 300,
    }}
  >
    <Box
      component="img"
      src={previewImage?.src}
      alt={previewImage?.name || 'Product'}
      sx={{
        maxWidth: '100%', maxHeight: '65vh',
        objectFit: 'contain', borderRadius: '8px',
        display: 'block',
      }}
    />
  </DialogContent>
</Dialog>




      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        PaperProps={{ sx: { borderRadius: '12px', minWidth: 380 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5, pt: 2.5, px: 3 }}>
          Delete shipping record?
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
            {deleteTarget?.approved
              ? 'This record is currently approved. Deleting it will remove all shipping information and cannot be undone.'
              : 'Are you sure you want to delete this shipping record? This action cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            variant="outlined"
            size="small"
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', height: 34 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            size="small"
            disabled={deleting}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none', height: 34 }}
          >
            {deleting ? 'Deleting…' : 'Delete Record'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ShippingTable;
